import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bot,
  Bug,
  Settings,
  LogOut,
  ChevronDown,
  ClipboardCheck,
  Globe,
  LayoutDashboard,
  Radar,
  Radio,
  Search,
  Server,
  Users,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useFindings } from "../../contexts/FindingsContext";
import { useSubdomainMonitorBadge } from "../../contexts/SubdomainMonitorBadgeContext";

// Display labels for the internal role values.
const ROLE_CONFIG = {
  pentester: { label: "Red Teamer", color: "#b91c1c", bg: "#fee2e2", border: "#fca5a5" },
  blue_team: { label: "Blue Teamer", color: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" },
  wasabian: { label: "Wasabian", color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
};

// Shared active/base link styling for the top bar.
const linkBase =
  "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap";
const linkActive = "text-wasabi-700 bg-wasabi-50 font-semibold";
const linkIdle = "text-slate-600 hover:text-slate-900 hover:bg-slate-100";

const TopNav = () => {
  const { logout, user, isPentester, isOperator, canViewOffensive } = useAuth();
  const { unseenCount } = useFindings();
  const { totalNew: subdomainNewCount } = useSubdomainMonitorBadge();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [openMenu, setOpenMenu] = useState(null); // section header or 'user'
  const navRef = useRef(null);

  // Close any open dropdown on outside click or route change.
  useEffect(() => setOpenMenu(null), [pathname]);
  useEffect(() => {
    const onDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const isActive = (path) => pathname === path || pathname.startsWith(path + "/");

  const badgeFor = (key) =>
    key === "findings-unseen" ? unseenCount : key === "subdomain-new" ? subdomainNewCount : 0;

  const topItems = [
    { path: "/dashboard", label: "Dashboard", icon: BarChart3, show: true },
    { path: "/pentests", label: "Penetration Tests", icon: ClipboardCheck, show: canViewOffensive },
    { path: "/agent-pentesting", label: "Agent Pentesting", icon: Bot, show: isPentester },
    { path: "/status", label: "Status", icon: LayoutDashboard, show: canViewOffensive },
  ].filter((i) => i.show);

  const sections = [
    {
      header: "Monitoring",
      items: [
        { path: "/subdomain-monitors", label: "Subdomain Monitoring", icon: Globe, badge: "subdomain-new", show: isOperator },
        { path: "/monitor/assets", label: "Asset Inventory", icon: Server, show: true },
        { path: "/monitor/findings", label: "Vulnerability Findings", icon: Bug, show: true },
      ],
    },
    {
      header: "OSINT",
      items: [
        { path: "/osint/leaks", label: "Leaks", icon: Search, show: isOperator },
        { path: "/osint/leak-sweep", label: "Leak-Site Sweep", icon: Radar, show: isOperator },
      ],
    },
    { header: "Infrastructure", items: [{ path: "/beacons", label: "Beacons", icon: Radio, show: isOperator }] },
    { header: "Administration", items: [{ path: "/users", label: "Users", icon: Users, show: isPentester }] },
  ]
    .map((s) => ({ ...s, items: s.items.filter((i) => i.show) }))
    .filter((s) => s.items.length > 0);

  const role = user?.role && ROLE_CONFIG[user.role];

  return (
    <header
      ref={navRef}
      className="h-14 bg-white border-b border-line px-4 flex items-center gap-1 sticky top-0 z-40 shadow-sm"
    >
      {/* Brand */}
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center gap-2 pr-3 mr-1 shrink-0"
      >
        <img src="/logos/wasabi-shinobi-mark-128.png" alt="Wasabi Shinobi" className="w-8 h-8 rounded-lg" />
        <span className="text-base font-bold tracking-tight text-slate-900 hidden sm:block">Wasabi Shinobi</span>
      </button>

      {/* Primary nav */}
      <nav className="flex items-center gap-0.5 flex-1 min-w-0 flex-wrap">
        {topItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive: a }) => `${linkBase} ${a ? linkActive : linkIdle}`}
            >
              <Icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          );
        })}

        {sections.map((section) => {
          // Single-item section -> direct link (label = item label).
          if (section.items.length === 1) {
            const item = section.items[0];
            const Icon = item.icon;
            const count = badgeFor(item.badge);
            return (
              <NavLink
                key={section.header}
                to={item.path}
                className={({ isActive: a }) => `${linkBase} ${a ? linkActive : linkIdle}`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
                {count > 0 && (
                  <span className="ml-1 text-[10px] font-bold text-white bg-wasabi-500 rounded-full px-1.5 py-0.5 leading-none">
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </NavLink>
            );
          }

          // Multi-item section -> dropdown.
          const anyActive = section.items.some((i) => isActive(i.path));
          const sectionBadge = section.items.reduce((n, i) => n + badgeFor(i.badge), 0);
          const open = openMenu === section.header;
          return (
            <div key={section.header} className="relative">
              <button
                onClick={() => setOpenMenu(open ? null : section.header)}
                className={`${linkBase} ${anyActive ? linkActive : linkIdle}`}
              >
                {section.header}
                {sectionBadge > 0 && <span className="w-1.5 h-1.5 rounded-full bg-wasabi-500" />}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="absolute left-0 mt-1 w-60 bg-white border border-line rounded-xl shadow-lg py-1 z-50">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const count = badgeFor(item.badge);
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive: a }) =>
                          `flex items-center gap-2.5 px-3 py-2 text-sm ${a ? "text-wasabi-700 bg-wasabi-50 font-semibold" : "text-slate-700 hover:bg-slate-50"}`
                        }
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="flex-1">{item.label}</span>
                        {count > 0 && (
                          <span className="text-[10px] font-bold text-white bg-wasabi-500 rounded-full px-1.5 py-0.5 leading-none">
                            {count > 99 ? "99+" : count}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User menu */}
      <div className="relative shrink-0 ml-2">
        <button
          onClick={() => setOpenMenu(openMenu === "user" ? null : "user")}
          className="flex items-center gap-2 pl-2 pr-1.5 py-1 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <span className="w-8 h-8 rounded-full bg-wasabi-500 flex items-center justify-center text-sm font-bold text-white">
            {user?.username?.[0]?.toUpperCase() || "U"}
          </span>
          <span className="hidden md:flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold text-slate-800">{user?.username || "User"}</span>
            {role && (
              <span
                className="text-[9px] font-bold uppercase tracking-wide px-1 rounded"
                style={{ color: role.color, backgroundColor: role.bg }}
              >
                {role.label}
              </span>
            )}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${openMenu === "user" ? "rotate-180" : ""}`} />
        </button>
        {openMenu === "user" && (
          <div className="absolute right-0 mt-1 w-56 bg-white border border-line rounded-xl shadow-lg py-1 z-50">
            <div className="px-3 py-2 border-b border-line">
              <p className="text-sm font-semibold text-slate-800 truncate">{user?.username || "User"}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email || ""}</p>
            </div>
            <NavLink
              to="/settings"
              className={({ isActive: a }) =>
                `flex items-center gap-2.5 px-3 py-2 text-sm ${a ? "text-wasabi-700 bg-wasabi-50 font-semibold" : "text-slate-700 hover:bg-slate-50"}`
              }
            >
              <Settings className="w-4 h-4" /> Settings
            </NavLink>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default TopNav;
